/**
 * A valid identity document of a health centre: a `valid-identity-document`
 * record, or a plain object with the same fields.
 * See docs/specs/features/valid-identity-documents.md.
 */
export interface ValidIdentityDocument {
  code: string;
  label: string;
  level: "high" | "low";
}

function byLabel(
  document: ValidIdentityDocument,
  otherDocument: ValidIdentityDocument
): number {
  return document.label.localeCompare(otherDocument.label, "fr");
}

/**
 * Splits the list into the high and low groups. In each group, the labels
 * are in alphabetical order: a hasMany does not keep the order of the API.
 */
export function groupValidIdentityDocumentsByLevel<
  Document extends ValidIdentityDocument
>(
  validIdentityDocuments: readonly Document[] | undefined
): { high: Document[]; low: Document[] } {
  const documents = [...(validIdentityDocuments ?? [])].sort(byLabel);
  return {
    high: documents.filter(document => document.level === "high"),
    low: documents.filter(document => document.level === "low")
  };
}

export function findValidIdentityDocument<
  Document extends ValidIdentityDocument
>(
  validIdentityDocuments: readonly Document[] | undefined,
  code: string | undefined
): Document | undefined {
  return validIdentityDocuments?.find(document => document.code === code);
}

/**
 * Returns the code itself when the list does not know it, so that a
 * recorded value is never shown with the label of another document.
 */
export function labelOfValidIdentityDocument(
  validIdentityDocuments: readonly ValidIdentityDocument[] | undefined,
  code: string
): string {
  return findValidIdentityDocument(validIdentityDocuments, code)?.label ?? code;
}
